import { FormInstance } from 'antd';
import { isEmpty } from 'lodash';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { InputActionMeta } from 'react-select';
import styled from 'styled-components';
import { UserType } from 'api/models';
import { Form } from 'components/atoms/form';
import { CreatableSelect } from 'components/atoms/select';
import { PlaceholderAvatar } from 'components/molecules/placeholder-avatar';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUsersQuery } from 'queries/members';
import { isValidEmail } from 'utils/is-valid-email';
import { userOption } from 'utils/project-fields/construct-user-option';
import { NewUserOption, UserOption } from 'utils/project-fields/types';
import {
  Col,
  Row,
  PlaceholderLabel,
  PlaceholderMeta,
  PlaceholderTaskCount,
  PlaceholderSectionHeading,
} from './styled';
import { CompanyOption, ProjectPlaceholder } from './types';

const PlaceholderSection = styled.div`
  margin-bottom: var(--spacing-06);
`;

const Label = styled.div`
  display: flex;
  flex-direction: column;
`;

const SecondaryText = styled.span`
  color: var(--gray-80);
  font-size: var(--font-size-sm);
  margin-top: var(--spacing-02);
`;
const ErrorMessage = styled.div`
  color: var(--red-60);
`;

const filterOption = ({ label = '', data }, input: string) => {
  const user: UserOption = data;

  return (
    user.__isNew__ ||
    label.toLowerCase().includes(input) ||
    (user.emailId ?? '').toLowerCase().includes(input)
  );
};

type CustomerInputKey = 'externalUsers';

export interface CustomerTeamStepFormValues {
  externalPlaceholders?: ProjectPlaceholder[];
  externalUsers?: UserOption[] | null;
}

interface Props {
  externalPlaceholders: ProjectPlaceholder[];
  form: FormInstance<CustomerTeamStepFormValues>;
  onValuesChange: (
    values: Partial<CustomerTeamStepFormValues>,
    allValues: CustomerTeamStepFormValues,
  ) => void;
  customer: CompanyOption;
  projectOwner: UserOption;
  externalUsers?: UserOption[];
}

export const newUserOption = (input: string): NewUserOption => {
  return {
    value: input,
    label: input,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __isNew__: true,
  };
};

const _getSuggestionsForInternalAssignment = (
  internalMembers,
  internalPlaceholders,
) => {
  const internalUsersByRole = internalMembers?.reduce((list, user) => {
    if (!user.roleId) {
      return list;
    }

    return {
      ...list,
      [user.roleId]: [...(list[user.roleId] || []), user],
    };
  }, {} as { [id: number]: UserOption[] });
  let lastAssignedInternalUserIndex = 0;

  return internalPlaceholders.map((_placeholder) => {
    if (!_placeholder.roleId || !internalUsersByRole?.[_placeholder.roleId]) {
      return _placeholder;
    }

    const user =
      internalUsersByRole[_placeholder?.roleId][lastAssignedInternalUserIndex];

    lastAssignedInternalUserIndex =
      lastAssignedInternalUserIndex >=
      internalUsersByRole[_placeholder.roleId].length - 1
        ? 0
        : lastAssignedInternalUserIndex + 1;

    return { ..._placeholder, user };
  });
};

const getSuggestionsForExternalAssignment = (
  externalMembers,
  externalPlaceholders,
) => {
  let lastAssignedExternalUserIndex = 0;

  return externalPlaceholders.map((_placeholder) => {
    if (!externalMembers || externalMembers.length <= 0) {
      return _placeholder;
    }

    const user = externalMembers[lastAssignedExternalUserIndex];

    lastAssignedExternalUserIndex =
      lastAssignedExternalUserIndex >= externalMembers.length - 1
        ? 0
        : lastAssignedExternalUserIndex + 1;

    return { ..._placeholder, user };
  });
};

export const CustomerTeamStep: FC<Props> = ({
  externalPlaceholders,
  form,
  onValuesChange,
  customer: selectedCustomer,
  externalUsers: externalMembers,
}) => {
  const { data: users = [] } = useUsersQuery();
  const [externalUser, setExternalUser] = useState('');
  const translate = useTranslate();
  const {
    user: { company: currentUserCompany },
  } = useUser();

  const noOptionsMessage = useTranslation(
    'projects.create.team_details.customer_champion.options.empty',
  );
  const formatCreateLabel = useTranslation('invitation.create.placeholder');
  const inValidEmailError = useTranslation('input.email');

  const externalUsers = users
    .filter((u) => u.company?.companyId === selectedCustomer.value)
    .filter((u) => u.userType === UserType.External);

  const externalUserEmails = externalUsers.map((option) => option.emailId);
  const existingUserEmails = users.map((user) => user.emailId);

  const toLowerCase = (str: string) => str?.toLowerCase();
  const isExistingEmailId = (emailId: string) => {
    return existingUserEmails.map(toLowerCase).includes(emailId.toLowerCase());
  };
  const getExistingUser = useCallback(
    (email) =>
      users.find(
        (user) =>
          user.emailId.toLowerCase() === (email ?? '').trim().toLowerCase(),
      ),
    [users],
  );
  const isExistingUserSameCompany = useCallback(
    (existingUser) =>
      existingUser.company.companyId === currentUserCompany.companyId,
    [currentUserCompany.companyId],
  );

  const handleNoOptionMessage = ({ inputValue }: { inputValue: string }) => {
    const existingUser = getExistingUser(inputValue);

    if (existingUser != null) {
      if (isExistingUserSameCompany(existingUser)) {
        return (
          <ErrorMessage>
            {translate('request.invitation.errors.part_of_current_user_team')}
          </ErrorMessage>
        );
      }

      return (
        <ErrorMessage>
          {translate('request.invitation.errors.part_of_another_team', {
            companyName: existingUser.company.companyName,
            type: existingUser.company.companyType,
          })}
        </ErrorMessage>
      );
    }

    return noOptionsMessage;
  };

  /**
   * If form is empty, prefill the placeholders with relevant assignees (matching roles)
   */
  if (
    form.getFieldValue('externalPlaceholders') == null ||
    form.getFieldValue('externalPlaceholders').length === 0
  ) {
    form.setFieldsValue({
      externalPlaceholders: getSuggestionsForExternalAssignment(
        externalMembers,
        externalPlaceholders,
      ),
    });
  }

  const onFormValuesChange = (changedValues, allValues) => {
    onValuesChange(changedValues, allValues);
  };
  // can only happen with new users
  const handleCSV = (input: string, key: CustomerInputKey) => {
    const csv = (input ?? '')
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);

    const validInputs = csv.filter(
      (csvItem) =>
        isValidEmail(csvItem) && !externalUserEmails.includes(csvItem),
    );

    if (!isEmpty(validInputs)) {
      form.setFieldsValue({
        [key]: [
          ...(form.getFieldValue(key) ?? []),
          ...validInputs.map((i) => ({
            label: i,
            value: i,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            __isNew__: true,
          })),
        ],
      });
    }
    setExternalUser('');
  };

  const handleInputChange =
    (stateUpdater: (input: string) => void, key?: CustomerInputKey) =>
    (input: string, action: InputActionMeta) => {
      if (action.action !== 'input-blur' && action.action !== 'menu-close') {
        stateUpdater(input);
        if (input.includes(',') && key) {
          handleCSV(input, key);
        }
      }
    };

  const handleUsersOfCustomerTeamOnBlur =
    (key: CustomerInputKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const actualInput = event.target.value;

      if (!actualInput) {
        form.setFields([
          {
            name: key,
            errors: [],
          },
        ]);

        return true;
      }

      const isValid = isValidEmail(actualInput);

      if (!isValid) {
        form.setFields([
          {
            name: key,
            errors: [inValidEmailError],
          },
        ]);

        return false;
      }

      const input = actualInput.toLowerCase().trim();
      const existingUser = getExistingUser(input);

      if (existingUser != null) {
        form.setFields([
          {
            name: key,
            errors: [
              isExistingUserSameCompany(existingUser)
                ? translate(
                    'request.invitation.errors.part_of_current_user_team',
                  )
                : translate('request.invitation.errors.part_of_another_team', {
                    companyName: existingUser.company.companyName,
                    type: existingUser.company.companyType,
                  }),
            ],
          },
        ]);

        return false;
      }

      const exitingUser = externalUsers?.find(
        (user) => user.emailId.toLowerCase() === input,
      );

      if (input && isValid) {
        const value = exitingUser
          ? userOption(exitingUser)
          : newUserOption(input);

        switch (key) {
          case 'externalUsers': {
            setExternalUser('');

            return form.setFieldsValue({
              [key]: [...(form.getFieldValue(key) ?? []), value],
            });
          }
          default:
            return false;
        }
      }

      return false;
    };

  return (
    <Form
      layout="vertical"
      form={form}
      name="stepPlaceholderForm"
      onValuesChange={onFormValuesChange}
    >
      {externalPlaceholders.length > 0 ? (
        <PlaceholderSection>
          <PlaceholderSectionHeading>
            <span>
              <FormattedMessage id="projects.create.team_details.external_team.placeholder.title" />
            </span>
          </PlaceholderSectionHeading>
          <Form.List name="externalPlaceholders">
            {(fields) => (
              <>
                {fields.map((field) => (
                  <Row>
                    <Col xs={8}>
                      <PlaceholderLabel>
                        <span>
                          <PlaceholderAvatar
                            isCustomerRole
                            name={externalPlaceholders[field.key]?.teamName}
                          />
                        </span>
                        <PlaceholderMeta>
                          <span>
                            {externalPlaceholders[field.key]?.teamName}
                          </span>
                          <PlaceholderTaskCount>
                            <FormattedMessage
                              id="projects.create.team_details.placeholder.task.assigned.label"
                              values={{
                                count:
                                  externalPlaceholders[field.key]?.taskCount,
                              }}
                            />
                          </PlaceholderTaskCount>
                        </PlaceholderMeta>
                      </PlaceholderLabel>
                    </Col>
                    <Col>
                      <Form.Item {...field} name={[field.name, 'user']}>
                        <CreatableSelect
                          isValidNewOption={(email) =>
                            isValidEmail(email) && !isExistingEmailId(email)
                          }
                          type="user"
                          isClearable
                          noOptionsMessage={handleNoOptionMessage}
                          placeholder={
                            <FormattedMessage id="projects.create.team_details.placeholders.input.placeholder" />
                          }
                          options={(externalUsers ?? []).map(userOption)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </Form.List>
        </PlaceholderSection>
      ) : null}
      <Row>
        <Col xs={8}>
          <Label>
            <FormattedMessage
              id="projects.create.team_details.invite_external_team"
              values={{
                company: selectedCustomer?.label || 'customer',
              }}
            />
            <SecondaryText>
              <FormattedMessage
                id="projects.create.team_details.invite_external_team.extras"
                values={{
                  company: selectedCustomer?.label || 'customer',
                }}
              />
            </SecondaryText>
          </Label>
        </Col>
        <Col>
          <Form.Item
            name="externalUsers"
            rules={[
              () => ({
                validator() {
                  if (!externalUser) {
                    return Promise.resolve();
                  }
                  const existingUser = getExistingUser(externalUser);

                  if (existingUser != null) {
                    return Promise.reject(
                      new Error(
                        isExistingUserSameCompany(existingUser)
                          ? translate(
                              'request.invitation.errors.part_of_current_user_team',
                            )
                          : translate(
                              'request.invitation.errors.part_of_another_team',
                              {
                                companyName: existingUser.company.companyName,
                                type: existingUser.company.companyType,
                              },
                            ),
                      ),
                    );
                  }

                  return Promise.reject(new Error(inValidEmailError));
                },
              }),
            ]}
          >
            <CreatableSelect
              isMulti
              isValidNewOption={(email) =>
                isValidEmail(email) && !isExistingEmailId(email)
              }
              inputValue={externalUser}
              onInputChange={handleInputChange(
                setExternalUser,
                'externalUsers',
              )}
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
              }}
              onBlur={handleUsersOfCustomerTeamOnBlur('externalUsers')}
              options={(users ?? [])
                .filter((u) => u.company?.companyId === selectedCustomer.value)
                .filter((u) => u.userType === UserType.External)
                .map(userOption)}
              isDisabled={false}
              type="user"
              isClearable
              placeholder={useTranslation('invitation.help.name_or_email')}
              noOptionsMessage={handleNoOptionMessage}
              formatCreateLabel={() => formatCreateLabel}
              filterOption={filterOption}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
